/**
 * Security utilities for the Art Emerge frontend application
 * Provides centralized security functions and validation
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - Raw HTML content
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return '';
  
  // Comprehensive XSS prevention
  return html
    // Remove script tags (including variations with whitespace/attributes)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<script\b[^>]*>/gi, '')
    // Remove event handlers (onclick, onerror, onload, etc.)
    .replace(/\s*on\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\s*on\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove javascript: URLs (including encoded variations)
    .replace(/javascript\s*:/gi, '')
    .replace(/&#x6a;&#x61;&#x76;&#x61;&#x73;&#x63;&#x72;&#x69;&#x70;&#x74;/gi, '') // hex encoded
    .replace(/&#106;&#97;&#118;&#97;&#115;&#99;&#114;&#105;&#112;&#116;/gi, '') // decimal encoded
    // Remove vbscript: and data: URLs in dangerous contexts
    .replace(/vbscript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
    // Remove style expressions (IE-specific but good to block)
    .replace(/expression\s*\(/gi, '')
    // Remove iframe, embed, object, applet tags (potential XSS/content injection)
    .replace(/<(iframe|embed|object|applet|meta|base|form)\b[^>]*>/gi, '')
    .replace(/<\/(iframe|embed|object|applet|meta|base|form)>/gi, '')
    // Remove SVG with embedded scripts
    .replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, (match) => {
      // Only allow SVG if it doesn't contain script or event handlers
      if (/on\w+\s*=|<script|javascript:/i.test(match)) {
        return '';
      }
      return match;
    });
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate URL format and ensure it's safe
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid and safe URL
 */
export const isValidURL = (url) => {
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

/**
 * Escape special characters for safe display
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text
 */
export const escapeHTML = (text) => {
  if (typeof text !== 'string') return '';
  
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Validate file upload security
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  } = options;

  const errors = [];

  // Check file size
  if (file.size > maxSize) {
    errors.push(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    errors.push(`File type ${file.type} is not allowed`);
  }

  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!allowedExtensions.includes(extension)) {
    errors.push(`File extension ${extension} is not allowed`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generate a secure random string
 * @param {number} length - Length of the string
 * @returns {string} - Random string
 */
export const generateSecureRandomString = (length = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomArray = new Uint8Array(length);
  crypto.getRandomValues(randomArray);
  
  for (let i = 0; i < length; i++) {
    result += chars[randomArray[i] % chars.length];
  }
  
  return result;
};

/**
 * Rate limiting utility for client-side operations
 */
export class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = new Map();
  }

  isAllowed(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    
    const keyRequests = this.requests.get(key);
    
    // Remove old requests outside the window
    const validRequests = keyRequests.filter(time => time > windowStart);
    this.requests.set(key, validRequests);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    return true;
  }
}

/**
 * Content Security Policy helpers
 */
export const CSP_DIRECTIVES = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", 'https://js.stripe.com'],
  'style-src': ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com'],
  'img-src': ["'self'", 'data:', 'https:', 'blob:'],
  'font-src': ["'self'", 'https://fonts.gstatic.com'],
  'connect-src': ["'self'", 'https://api.stripe.com', 'wss:'],
  'frame-src': ["'self'", 'https://js.stripe.com'],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"]
};

/**
 * Input validation utilities
 */
export const validateInput = {
  /**
   * Validate artwork title
   */
  artworkTitle: (title) => {
    if (!title || typeof title !== 'string') return false;
    return title.trim().length >= 1 && title.trim().length <= 200;
  },

  /**
   * Validate artwork description
   */
  artworkDescription: (description) => {
    if (!description || typeof description !== 'string') return false;
    return description.trim().length >= 10 && description.trim().length <= 2000;
  },

  /**
   * Validate price
   */
  price: (price) => {
    const numPrice = parseFloat(price);
    return !isNaN(numPrice) && numPrice >= 0 && numPrice <= 1000000;
  },

  /**
   * Validate dimensions
   */
  dimensions: (width, height, depth) => {
    const w = parseFloat(width);
    const h = parseFloat(height);
    const d = parseFloat(depth);
    
    return !isNaN(w) && !isNaN(h) && !isNaN(d) &&
           w > 0 && h > 0 && d >= 0 &&
           w <= 1000 && h <= 1000 && d <= 1000;
  }
}; 